import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import type { Assignee, VisitListResponse } from '@fitting-room/contracts'
import { CalendarDays, ChevronLeft, ChevronRight, List, Plus, Search } from 'lucide-react'
import { AppointmentActionMenu } from '../../components/admin/AppointmentActionMenu'
import { AppointmentDrawer } from '../../components/admin/AppointmentDrawer'
import { AssigneeFilterPopover, UNASSIGNED } from '../../components/admin/AssigneeFilterPopover'
import { CalendarMonthView } from '../../components/admin/CalendarMonthView'
import { Chip } from '../../components/admin/Chip'
import { DatePicker } from '../../components/admin/DatePicker'
import { NewAppointmentDialog } from '../../components/admin/NewAppointmentDialog'
import { PageHeader } from '../../components/admin/PageHeader'
import { StaffMark } from '../../components/admin/StaffMark'
import { SessionPill, VisitPill } from '../../components/admin/StatusPill'
import type { AppointmentFilters } from '../../lib/api'
import { useAppointmentTypes, useAppointments, useAppointmentsInMonth, useAssignees, useBackOffice } from '../../lib/adminQueries'
import { VISIT_STATUS_LABEL, VISIT_STATUS_ORDER, assigneeName } from '../../lib/appointments'
import { BOUTIQUE_LOCALE, boutiqueDayOf, boutiqueTime, boutiqueToday } from '../../lib/boutique'
import { formatDay } from '../../lib/format'
import { monthOf, shiftDay } from '../../lib/months'
import { useStaffToken } from '../../lib/staffToken'
import { activityCounts, filterByActivity, walkNeighbours } from './visitFilter'

type VisitRow = VisitListResponse['visits'][number]

const SESSION_FILTERS = [
  { key: 'submitted', label: 'List sent' },
  { key: 'open', label: 'Link open' },
  { key: 'none', label: 'No link' },
  { key: 'expired', label: 'Appointment passed' },
] as const

/** How far the list reaches when nobody has picked dates: today and the month ahead, as tperent's does. */
const DEFAULT_SPAN_DAYS = 30

function csv(raw: string | null): string[] {
  return (raw ?? '').split(',').filter(Boolean)
}

/** Whole days from `from` to `to`, counting both. */
function spanOf(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
}

/**
 * One appointment, as a row you can hit with a thumb — and right-click, or
 * long-press, to change its status where it is.
 *
 * After tperent's list row: the time and the name are what a stylist scans
 * for, then the type, who has it, and the two statuses — the appointment's
 * and its fitting room's.
 */
function AppointmentRow({
  visit,
  typeLabel,
  durationMinutes,
  color,
  members,
  onOpen,
  pickup = false,
}: {
  readonly visit: VisitRow
  readonly typeLabel: string
  readonly durationMinutes: number | undefined
  readonly color: string | undefined
  readonly members: readonly Assignee[]
  readonly onOpen: () => void
  /** Collecting a gown: what it says is which order, not what was picked. */
  readonly pickup?: boolean
}) {
  const picks = Object.values(visit.pickCounts).reduce((n, v) => n + v, 0)
  const member = members.find((m) => m.id === visit.assigneeId)
  const minutes = visit.durationMinutes ?? durationMinutes
  const end = minutes ? new Date(new Date(visit.scheduledAt).getTime() + minutes * 60_000).toISOString() : undefined
  return (
    <AppointmentActionMenu visitId={visit.id} status={visit.status} announceMenu>
      <button type="button" className="st-card-action st-row mb-2 px-4 py-3" onClick={onOpen} data-visit-id={visit.id} data-status={visit.status}>
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <div className="flex min-w-0 items-baseline gap-3">
            <span className="st-display shrink-0 text-lg tabular-nums">
              {boutiqueTime(visit.scheduledAt)}
              {end ? <span className="st-meta text-xs"> – {boutiqueTime(end)}</span> : null}
            </span>
            <span className="st-row-name truncate font-medium">{visit.customerName ?? 'No name'}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <VisitPill status={visit.status} />
            {pickup ? null : <SessionPill status={visit.sessionStatus} />}
          </div>
        </div>
        <div className="st-meta mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="flex items-center gap-1.5">
            <span className="st-tier-dot" style={color ? { background: color } : undefined} aria-hidden />
            {typeLabel}
          </span>
          {visit.customerPhone ? <span className="tabular-nums">{visit.customerPhone}</span> : null}
          {pickup ? (
            visit.orders.length > 0 ? (
              <span className="tabular-nums">{visit.orders.map((order) => order.orderNo).join(', ')}</span>
            ) : (
              <span className="text-[color:#8d3f2c]" data-testid="pickup-unlinked">
                No order linked
              </span>
            )
          ) : (
            <span>{picks > 0 ? `${picks} picked` : 'nothing picked yet'}</span>
          )}
          <span className="ml-auto flex items-center gap-1">
            {visit.assigneeId ? (
              <>
                <StaffMark mark={member?.calendarMark} name={member?.displayName} />
                {assigneeName(members, visit.assigneeId)}
              </>
            ) : (
              <span className="text-[color:var(--st-ink-faint)]">Unassigned</span>
            )}
          </span>
        </div>
      </button>
    </AppointmentActionMenu>
  )
}

/**
 * Appointments: the list and the month, ported from tperent's appointments
 * and calendar tabs onto the console's own day sheet.
 *
 * Every filter lives in the URL, so a filtered list survives a reload and can
 * be sent to a colleague — tperent's rule. The server applies status,
 * assignee, source and search to both views; the fitting room chips beside
 * them are counted and applied here, over what the server sent.
 */
export function VisitListPage() {
  const { token, identity } = useStaffToken()
  const [params, setParams] = useSearchParams()
  const locale = BOUTIQUE_LOCALE
  const today = boutiqueToday()

  const view = params.get('view') === 'calendar' ? 'calendar' : 'list'
  // `date` is the old day sheet's address; a bookmark of it opens that one day.
  const from = params.get('from') ?? params.get('date') ?? today
  const to = params.get('to') ?? (params.get('date') ? from : shiftDay(from, DEFAULT_SPAN_DAYS))
  const month = params.get('month') ?? monthOf(from)
  const openVisitId = params.get('visit')
  const [tones, setTones] = useState<string[]>([])
  const [creating, setCreating] = useState(false)

  const filters: AppointmentFilters = useMemo(
    () => ({
      statuses: csv(params.get('status')),
      assignees: csv(params.get('assignee')),
      sources: csv(params.get('source')),
      q: params.get('q') ?? '',
    }),
    [params],
  )

  const types = useAppointmentTypes(token).data?.types
  const settings = useBackOffice(token).data
  const members = useAssignees(token).data?.members ?? []
  const typeLabels = useMemo(() => new Map((types ?? []).map((t) => [t.key, t.label])), [types])

  // Only the view on screen is fetched: a month is thirty times a day, and the
  // console is opened on a tablet over the boutique's own wifi.
  const listQuery = useAppointments(view === 'list' ? token : null, { from, to }, filters)
  const monthQuery = useAppointmentsInMonth(view === 'calendar' ? token : null, month, filters)
  const query = view === 'calendar' ? monthQuery : listQuery

  const visits = useMemo(() => query.data?.visits ?? [], [query.data])

  // A pickup has no fitting room, so it is in none of the fitting room's chips: not "No link", which asks for one to be sent.
  const pickups = useMemo(() => new Set((types ?? []).filter((type) => type.kind === 'pickup').map((type) => type.key)), [types])

  const filtered = useMemo(() => filterByActivity(visits, tones, pickups), [visits, tones, pickups])

  const counts = useMemo(() => activityCounts(visits, pickups), [visits, pickups])

  // The drawer walks whatever list opened it, so its arrows agree with what is
  // on screen behind it rather than with the unfiltered range.
  const walk = view === 'calendar' ? visits : filtered
  const { current: openVisit, previous: previousVisit, next: nextVisit } = walkNeighbours(walk, openVisitId)

  const byDay = useMemo(() => {
    const groups: Array<{ day: string; visits: VisitRow[] }> = []
    for (const visit of filtered) {
      const day = boutiqueDayOf(visit.scheduledAt)
      const last = groups[groups.length - 1]
      if (last?.day === day) last.visits.push(visit)
      else groups.push({ day, visits: [visit] })
    }
    return groups
  }, [filtered])

  const setParam = (updates: Record<string, string | undefined>) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined || value === '') next.delete(key)
          else next.set(key, value)
        }
        return next
      },
      { replace: true },
    )
  }

  const setRange = (nextFrom: string, nextTo: string) =>
    setParam({ from: nextFrom, to: nextTo < nextFrom ? nextFrom : nextTo, date: undefined })

  const toggleIn = (key: 'status' | 'assignee', value: string) => {
    const current = csv(params.get(key))
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    setParam({ [key]: next.join(',') })
  }

  const toggleTone = (key: string) =>
    setTones((current) => (current.includes(key) ? current.filter((t) => t !== key) : [...current, key]))

  const span = spanOf(from, to)
  const assignees = filters.assignees ?? []
  const me = identity?.memberId

  return (
    <div>
      <PageHeader eyebrow="At the boutique" title="Appointments">
        {/* Two chips rather than a segmented control: the console already says
            "on / off" with `aria-pressed` everywhere else, and a third idiom
            for the same idea is a third thing to learn. */}
        <Chip pressed={view === 'list'} onClick={() => setParam({ view: undefined })}>
          <List size={13} aria-hidden />
          List
        </Chip>
        <Chip pressed={view === 'calendar'} onClick={() => setParam({ view: 'calendar', month })}>
          <CalendarDays size={13} aria-hidden />
          Month
        </Chip>
        <button type="button" className="st-btn" onClick={() => setCreating(true)}>
          <Plus size={14} aria-hidden />
          New appointment
        </button>
      </PageHeader>

      <div className="print-hide mb-4 flex flex-col gap-2.5">
        <SearchBox value={filters.q ?? ''} onChange={(q) => setParam({ q })} />

        {view === 'list' ? (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="st-iconbtn" onClick={() => setRange(shiftDay(from, -span), shiftDay(to, -span))} aria-label="Earlier">
              <ChevronLeft size={16} aria-hidden />
            </button>
            <DatePicker value={from} onChange={(day) => setRange(day, to < day ? day : to)} label="From" />
            <span className="st-meta">to</span>
            <DatePicker value={to} onChange={(day) => setRange(from > day ? day : from, day)} label="To" />
            <button type="button" className="st-iconbtn" onClick={() => setRange(shiftDay(from, span), shiftDay(to, span))} aria-label="Later">
              <ChevronRight size={16} aria-hidden />
            </button>
            <Chip pressed={from === today && to === today} onClick={() => setRange(today, today)}>
              Today
            </Chip>
            <Chip pressed={from === today && to === shiftDay(today, 6)} onClick={() => setRange(today, shiftDay(today, 6))}>
              This week
            </Chip>
            <Chip
              pressed={from === today && to === shiftDay(today, DEFAULT_SPAN_DAYS)}
              onClick={() => setRange(today, shiftDay(today, DEFAULT_SPAN_DAYS))}
            >
              Next 30 days
            </Chip>
          </div>
        ) : null}

        {/* Who has it. "Mine", "Unassigned" and the popover are three ways
            into ONE set, so "mine and nobody's yet" is a combination rather
            than two filters fighting (tperent's AssigneeFilterPopover). */}
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by who has it">
          {me ? (
            <Chip pressed={assignees.includes(me)} onClick={() => toggleIn('assignee', me)}>
              Mine
            </Chip>
          ) : null}
          <Chip pressed={assignees.includes(UNASSIGNED)} onClick={() => toggleIn('assignee', UNASSIGNED)}>
            Unassigned
          </Chip>
          <AssigneeFilterPopover members={members} selected={assignees} onChange={(next) => setParam({ assignee: next.join(',') })} />
          <select
            className="st-select ml-1"
            aria-label="Source"
            value={filters.sources?.[0] ?? ''}
            onChange={(event) => setParam({ source: event.target.value })}
          >
            <option value="">Any source</option>
            {(settings?.bookingSources ?? []).map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by status">
            {VISIT_STATUS_ORDER.map((status) => (
              <Chip key={status} pressed={(filters.statuses ?? []).includes(status)} onClick={() => toggleIn('status', status)}>
                {VISIT_STATUS_LABEL[status]}
              </Chip>
            ))}
          </span>
          {/* The fitting room's own state, counted over what the server sent
              and applied here. The list only: the month is for seeing the
              shape of the weeks, not for working a day. */}
          {view === 'list' ? (
            <span className="ml-auto flex flex-wrap gap-1.5" role="group" aria-label="Filter by fitting room">
              {SESSION_FILTERS.map((filter) => (
                <Chip key={filter.key} pressed={tones.includes(filter.key)} onClick={() => toggleTone(filter.key)} count={counts.get(filter.key) ?? 0}>
                  {filter.label}
                </Chip>
              ))}
            </span>
          ) : null}
        </div>
      </div>

      {query.isError ? (
        <p className="st-alert mb-4">Couldn&rsquo;t load appointments. Check your sign-in and try again.</p>
      ) : null}

      {view === 'list' ? (
        <>
          {query.isPending ? <p className="st-meta">Loading…</p> : null}

          {!query.isPending && filtered.length === 0 ? (
            <div className="st-card px-4 py-10 text-center">
              <p className="st-meta">
                {visits.length === 0
                  ? from === to
                    ? `Nothing booked for ${formatDay(from, locale)}.`
                    : `Nothing booked from ${formatDay(from, locale)} to ${formatDay(to, locale)} that matches.`
                  : 'No appointment in these dates matches those filters.'}
              </p>
            </div>
          ) : null}

          {byDay.map((group) => (
            <section key={group.day} className="mb-4">
              <h2 className="st-eyebrow mb-2">
                {group.day === today ? 'Today · ' : ''}
                {formatDay(group.day, locale)}
                <span className="ml-2 tabular-nums">{group.visits.length}</span>
              </h2>
              {group.visits.map((visit) => {
                const type = types?.find((t) => t.key === visit.tierKey)
                return (
                  <AppointmentRow
                    key={visit.id}
                    visit={visit}
                    typeLabel={type?.label ?? visit.tierKey}
                    durationMinutes={type?.durationMinutes}
                    color={settings?.tierColors[visit.tierKey]}
                    members={members}
                    onOpen={() => setParam({ visit: visit.id })}
                    pickup={type?.kind === 'pickup'}
                  />
                )
              })}
            </section>
          ))}
        </>
      ) : (
        <CalendarMonthView
          month={month}
          visits={walk}
          loading={monthQuery.isPending}
          onMonthChange={(next) => setParam({ month: next })}
          onOpenVisit={(id) => setParam({ visit: id })}
          // Opening a day is the list over that day, and closes the drawer:
          // carrying one appointment onto another day's list leaves the URL
          // describing two places at once.
          onOpenDay={(day) => setParam({ view: undefined, from: day, to: day, date: undefined, visit: undefined })}
          tierColors={settings?.tierColors}
          tierLabels={typeLabels}
          members={members}
        />
      )}

      <AppointmentDrawer
        visitId={openVisitId}
        row={openVisit}
        onClose={() => setParam({ visit: undefined })}
        onPrevious={previousVisit ? () => setParam({ visit: previousVisit.id }) : undefined}
        onNext={nextVisit ? () => setParam({ visit: nextVisit.id }) : undefined}
      />

      <NewAppointmentDialog
        open={creating}
        initialDate={from < today ? today : from}
        onClose={() => setCreating(false)}
        onOpenAppointment={(id) => {
          setCreating(false)
          setParam({ visit: id })
        }}
      />

      {view === 'list' && filtered.length > 0 ? (
        <p className="st-meta mt-6 text-xs">Right-click an appointment, or press and hold it, to change its status.</p>
      ) : null}
    </div>
  )
}

/**
 * Search, debounced into the URL. Typing a name should not be a request per
 * keystroke, and the URL is where every other filter lives.
 */
function SearchBox({ value, onChange }: { readonly value: string; readonly onChange: (q: string) => void }) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  useEffect(() => {
    if (draft === value) return
    const timer = setTimeout(() => onChange(draft.trim()), 300)
    return () => clearTimeout(timer)
    // `onChange` is a fresh closure every render; the draft is what matters.
  }, [draft])
  return (
    <label className="relative block">
      <Search size={14} className="st-meta pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" aria-hidden />
      <input
        className="st-input st-input-search w-full pl-8"
        type="search"
        placeholder="Search name, phone or email"
        aria-label="Search appointments"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
    </label>
  )
}
